import { useEffect, useMemo, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";
import {
  apiErrorFormIssue,
  FormErrorSummary,
  focusValidationControl,
  type FormValidationIssue,
} from "@/components/ui/form-validation";
import { formatDateOnlyDisplay } from "@/lib/date-only";

import type { PersonScheduleView } from "@/api/people";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { PersonFormFields } from "@/components/people/PersonFormFields";
import { collectPersonValidationIssues } from "@/components/people/personValidationIssues";
import {
  PeopleActionRow,
  PeopleActionsController,
} from "@/components/people/PeopleActionsController";
import {
  buildPersonPatch,
  getPersonFormDefaults,
  personEditSchema,
  type PersonEditFormValues,
  type PersonFormValues,
} from "@/components/people/personFormModel";
import { ConfirmDialog } from "@/components/templates/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Surface } from "@/components/ui/surface";
import { usePatchPerson } from "@/hooks/usePeople";

interface Props {
  person: PersonScheduleView;
  editing: boolean;
  onEditChange: (editing: boolean) => void;
  canManageProjectManager: boolean;
}

export function PersonProfileSection({
  person,
  editing: requestedEditing,
  onEditChange,
  canManageProjectManager,
}: Props): React.ReactElement {
  const { t, i18n } = useTranslation();
  const [discardOpen, setDiscardOpen] = useState(false);
  const [serverError, setServerError] = useState<FormValidationIssue | null>(null);
  const patchPerson = usePatchPerson();
  const canEdit = person.action_capabilities.edit.state === "enabled";
  const editing = requestedEditing && canEdit;
  const dateLocale = i18n.resolvedLanguage?.startsWith("en") ? "en" : "ru";
  const defaults = useMemo(() => getPersonFormDefaults({ mode: "edit", user: person }), [person]);
  const form = useForm<PersonFormValues>({
    resolver: zodResolver(personEditSchema) as Resolver<PersonFormValues>,
    defaultValues: defaults,
    mode: "onChange",
  });
  const validationIssues = collectPersonValidationIssues(
    form.formState.errors as Record<string, { message?: unknown } | undefined>,
    form.getValues("group"),
    t,
  );
  const issues: FormValidationIssue[] = [
    ...validationIssues,
    ...(serverError ? [serverError] : []),
  ];
  useReleaseDirtyState(form.formState.isDirty);

  useEffect(() => {
    if (!editing) form.reset(defaults);
  }, [defaults, editing, form]);

  const exitEdit = (): void => {
    form.reset(defaults);
    setServerError(null);
    setDiscardOpen(false);
    onEditChange(false);
  };
  const cancel = (): void => {
    if (form.formState.isDirty) setDiscardOpen(true);
    else exitEdit();
  };
  const submit = form.handleSubmit(
    async (values) => {
      setServerError(null);
      try {
        await patchPerson.mutateAsync({
          id: person.id,
          body: buildPersonPatch(values as PersonEditFormValues, form.formState.dirtyFields),
        });
        toast.success(t("admin.users.toast.saved"));
        onEditChange(false);
      } catch (error) {
        setServerError(
          apiErrorFormIssue(error, {
            id: "person-profile-server-error",
            fallbackMessage: t("common.apiError.fallback"),
          }),
        );
        showApiErrorToast(error, { fallbackMessage: t("common.apiError.fallback") });
      }
    },
    (invalidErrors) => {
      const first = collectPersonValidationIssues(
        invalidErrors as Record<string, { message?: unknown } | undefined>,
        form.getValues("group"),
        t,
      )[0]?.controlId;
      if (first) requestAnimationFrame(() => focusValidationControl(first));
    },
  );

  return (
    <Surface className="mt-8 overflow-hidden" data-testid="person-profile">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--rule-line)] px-4 py-3">
        <div className="flex items-center gap-2">
          <h2 className="font-heading text-base font-semibold">{t("people.profileTitle")}</h2>
          <Badge tone={person.is_active ? "success" : "neutral"}>
            {person.is_active ? t("people.profileStatusActive") : t("people.profileStatusInactive")}
          </Badge>
        </div>
        {!editing && (
          <div className="flex w-full flex-wrap items-center gap-1 sm:w-auto sm:justify-end">
            {canEdit && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="person-profile-edit"
                onClick={() => onEditChange(true)}
              >
                <Pencil aria-hidden="true" />
                {t("admin.users.actions.edit")}
              </Button>
            )}
            <PeopleActionsController person={person}>
              {(controls) => (
                <PeopleActionRow person={person} controls={controls} showEdit={false} />
              )}
            </PeopleActionsController>
          </div>
        )}
      </div>

      {editing ? (
        <form onSubmit={submit} data-testid="person-profile-form">
          <PersonFormFields
            mode="edit"
            form={form}
            user={person}
            canManageProjectManager={canManageProjectManager}
            validationIssues={validationIssues}
          />
          <div className="px-4 pb-3">
            <FormErrorSummary
              issues={issues}
              countLabel={(count) => t("validation.fieldErrorCount", { count })}
              data-testid="person-profile-error-summary"
            />
          </div>
          <footer className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-[var(--rule-line)] bg-popover px-4 py-3">
            <span className="text-muted-foreground text-xs">
              {form.formState.isDirty ? t("people.profileDirty") : t("people.profileClean")}
            </span>
            <div className="flex-1" />
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="person-profile-cancel"
              onClick={cancel}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              size="sm"
              data-testid="person-profile-save"
              disabled={!form.formState.isDirty || patchPerson.isPending}
            >
              {t("common.save")}
            </Button>
          </footer>
        </form>
      ) : (
        <dl
          className="grid gap-x-6 gap-y-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="person-profile-read"
        >
          <ReadValue label={t("people.fields.name")} value={person.full_name} />
          <ReadValue
            label={t("admin.users.fields.tabNumber")}
            value={person.tab_number ?? t("people.fields.none")}
          />
          <ReadValue
            label={t("people.fields.unit")}
            value={person.unit_name ?? t("people.fields.none")}
          />
          <ReadValue
            label={t("people.fields.position")}
            value={person.position_name ?? t("people.fields.none")}
          />
          <ReadValue
            label={t("people.fields.calendar")}
            value={person.profile.calendar_name ?? t("people.fields.calendarInherited")}
          />
          <ReadValue
            label={t("admin.users.fields.tracksTime")}
            value={person.tracks_time ? t("people.fields.yes") : t("people.fields.no")}
          />
          <ReadValue
            label={t("admin.users.fields.projectManager")}
            value={person.is_project_manager ? t("people.fields.yes") : t("people.fields.no")}
          />
          <ReadValue label={t("admin.users.fields.role")} value={t(`role.${person.role}`)} />
          <ReadValue
            label={t("admin.users.fields.email")}
            value={person.web_email ?? t("people.fields.none")}
          />
          <ReadValue
            label={t("people.fields.lineManager")}
            value={person.profile.line_manager_name ?? t("people.fields.none")}
          />
          <ReadValue
            label={t("admin.users.fields.hireDate")}
            value={
              person.hire_date
                ? formatDateOnlyDisplay(person.hire_date, dateLocale)
                : t("people.fields.none")
            }
          />
          <ReadValue
            label={t("admin.users.fields.terminationDate")}
            value={
              person.termination_date
                ? formatDateOnlyDisplay(person.termination_date, dateLocale)
                : t("people.fields.none")
            }
          />
        </dl>
      )}

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("people.profileDiscardTitle")}
        body={t("people.profileDiscardBody")}
        submitLabel={t("people.profileDiscardAction")}
        destructive
        onConfirm={exitEdit}
        testId="person-profile-discard"
      />
    </Surface>
  );
}

function ReadValue({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs">{label}</dt>
      <dd className="mt-1 break-words text-sm font-medium">{value}</dd>
    </div>
  );
}
